import { useEffect, useState, type FormEvent } from 'react';
import { EVENT_SURVEY_LIMITS } from '@icebreaker/shared';
import { Button, Field, Modal, Textarea, useFormErrors } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { disableEventSurvey, surveyDisableReset } from '../store/organization-post-event.slice';
import { ServerBanner, UnknownOutcome, failureKind, fieldErrorPaths } from './dialog-parts';
import styles from './organization-post-event.module.css';

const REASON_MIN = EVENT_SURVEY_LIMITS.disableReasonMin;
const REASON_MAX = EVENT_SURVEY_LIMITS.disableReasonMax;

/**
 * "ANKETİ KAPAT" (belge ve anket Story 8, tasarım kare 11b/11c). Gerekçe
 * zorunludur: boşken ya da kısa iken gönderilmez, hata alanın altında ve alanla
 * ilişkili yazılır (sunucu da reddeder). Son onay kırmızı dolgudur (düğme
 * standardı, geri alınamaz). Gerekçe denetim defterine personelin adıyla yazılır.
 *
 * 409 (anket bu arada kapandı ya da zaten kapatıldı) diyaloğu kapatır; liste
 * sagada yeniden çekilir ve satır gerçek hâli gösterir. İkinci kayıt oluşmaz.
 */
export function CloseSurveyDialog({
  organizationId,
  eventId,
  eventTitle,
  onCancel,
  onClosed,
}: {
  organizationId: string;
  eventId: string;
  eventTitle: string;
  onCancel: () => void;
  /** Başarıda kapatılan etkinliğin adı, 409'da `null` (bildirim yazılmaz). */
  onClosed: (title: string | null) => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const disable = useAppSelector((root: RootState) => root.panelOrgPostEvent.disable);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  /** Diyalog yalnız KENDİ gönderiminin sonucuyla kapanır (kapak kaldırma emsali). */
  const [sent, setSent] = useState(false);

  useEffect(() => {
    dispatch(surveyDisableReset());
  }, [dispatch]);

  const own = disable.eventId === eventId;
  const conflict = own && disable.phase === 'failed' && disable.error?.statusCode === 409;
  useEffect(() => {
    if (!sent || !own) return;
    if (disable.phase === 'succeeded') onClosed(eventTitle);
    else if (conflict) onClosed(null);
  }, [conflict, disable.phase, eventTitle, onClosed, own, sent]);

  const busy = own && disable.phase === 'loading';
  const outcome = own ? failureKind(disable.phase, disable.error) : 'none';
  const serverPaths = fieldErrorPaths(own ? disable.error : null);
  const serverForm = useFormErrors(own ? disable.error : null, ['reason']);
  const trimmed = reason.trim();
  const localError =
    trimmed === ''
      ? t.t('admin.orgs.postEvent.reasonEmpty')
      : trimmed.length < REASON_MIN
        ? t.t('admin.orgs.postEvent.reasonShort', { count: REASON_MIN })
        : undefined;
  const reasonError = serverPaths.has('reason')
    ? (serverForm.at('reason') ?? t.t('admin.orgs.postEvent.reasonShort', { count: REASON_MIN }))
    : touched
      ? localError
      : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (busy || localError !== undefined) return;
    setSent(true);
    dispatch(disableEventSurvey.request({ organizationId, eventId, reason: trimmed }));
  };

  return (
    <Modal
      open
      title={t.t('admin.orgs.postEvent.dialogTitle')}
      closeLabel={t.t('common.close')}
      onClose={onCancel}
      closeDisabled={busy}
      footer={
        <div className={styles.footerActions}>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            {t.t('admin.orgs.postEvent.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="close-survey-form"
            loading={busy}
            loadingLabel={t.t('admin.orgs.postEvent.submitting')}
          >
            {outcome !== 'none' ? t.t('admin.orgs.retry') : t.t('admin.orgs.postEvent.submit')}
          </Button>
        </div>
      }
    >
      <p className={styles.dialogBody}>{t.t('admin.orgs.postEvent.dialogBody', { eventTitle })}</p>
      <form id="close-survey-form" className={styles.form} onSubmit={submit} noValidate>
        <Field
          id="close-survey-reason"
          label={
            <>
              {t.t('admin.orgs.postEvent.reason')}{' '}
              <span className={styles.required}>{t.t('admin.orgs.postEvent.reasonRequired')}</span>
            </>
          }
          hint={t.t('admin.orgs.postEvent.reasonHint')}
          error={reasonError}
        >
          <Textarea
            id="close-survey-reason"
            rows={3}
            value={reason}
            maxLength={REASON_MAX}
            required
            aria-required="true"
            disabled={busy}
            invalid={Boolean(reasonError)}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        {outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.orgs.postEvent.reloaded')} />
        ) : outcome === 'rejected' && !conflict ? (
          <ServerBanner>{t.t('admin.orgs.postEvent.serverError')}</ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}
